import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css, keyframes } from '@emotion/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import useMountedState from 'react-use/lib/useMountedState';
import { loadImage } from '../../../utils/imageUtils';
import { LARGE_FEATURE_SHOWCASE_TEXT_CSS, MEDIUM_FEATURE_SHOWCASE_TEXT_CSS } from './featureShowcaseStyles';
const IMAGE_WIDTH_PX = 400;
const IMAGE_HEIGHT_PX = 400;
const DELAY_MS = 2000;
const IMAGE_CONTAINER_SIZE_PX = 200;
const START_Y_PERC = 48;
const IMAGE_KEYFRAMES = keyframes `
  0% {
    transform: translateX(-50%) translateY(-${START_Y_PERC}%);
  }
  50% {
    transform: translateX(-50%) translateY(-50%);
  }
  100% {
    transform: translateX(-50%) translateY(-${START_Y_PERC}%);
  }
  `;
const IMAGE_CONTAINER_CSS = css `
  position: relative;
  width: ${IMAGE_CONTAINER_SIZE_PX}px;
  height: ${IMAGE_CONTAINER_SIZE_PX}px;
`;
const IMAGE_CSS = css `
  position: absolute;
  transform: translateX(-50%) translateY(-${START_Y_PERC}%);
  left: ${Math.round(IMAGE_CONTAINER_SIZE_PX / 2)}px;
  top: ${Math.round(IMAGE_CONTAINER_SIZE_PX / 2)}px;
  animation: ${IMAGE_KEYFRAMES} 2s ease-in-out infinite alternate;
  filter: drop-shadow(10px 10px 0 rgba(0, 0, 0, 0.15));
`;
const TEXT_CONTAINER_CSS = css `
  filter: drop-shadow(3px 3px 0 rgba(0, 0, 0, 0.5));
`;
export const AvatarShowcase = ({ title, subTitle, imageUrls, isAvatarOnLeft }) => {
    const isMounted = useMountedState();
    const currImageIndexRef = useRef();
    const [imageUrl, setImageUrl] = useState();
    const showImageIndex = useCallback(async (imageIndex) => {
        if (!isMounted()) {
            return;
        }
        currImageIndexRef.current = imageIndex;
        await loadImage(imageUrls[currImageIndexRef.current]);
        if (!isMounted()) {
            return;
        }
        setImageUrl(imageUrls[currImageIndexRef.current]);
        const nextIndex = (currImageIndexRef.current + 1) % imageUrls.length;
        loadImage(imageUrls[nextIndex]); // start preloading next image (but do not await it)
        setTimeout(() => {
            showImageIndex(nextIndex);
        }, DELAY_MS);
    }, [imageUrls, isMounted]);
    useEffect(() => {
        // get it started
        if (currImageIndexRef.current === undefined) {
            showImageIndex(0);
        }
    }, [showImageIndex, isMounted]);
    if (!imageUrl) {
        return null;
    }
    return (_jsxs("div", { css: css `
        display: flex;
        flex-direction: ${isAvatarOnLeft ? 'row' : 'row-reverse'};
        flex-wrap: wrap;
        gap: 10px;
        align-items: center;
        justify-content: center;
        width: 100%;
        padding: 20px;
      `, children: [_jsx("div", { css: IMAGE_CONTAINER_CSS, children: _jsx("img", { src: imageUrl, css: IMAGE_CSS, alt: 'Avatar', width: IMAGE_WIDTH_PX, height: IMAGE_HEIGHT_PX }) }), _jsxs("div", { css: TEXT_CONTAINER_CSS, children: [_jsx("div", { css: LARGE_FEATURE_SHOWCASE_TEXT_CSS, children: title }), _jsx("div", { css: MEDIUM_FEATURE_SHOWCASE_TEXT_CSS, children: subTitle })] })] }));
};
